import { Link } from "react-router-dom";
import { Eye, Heart, Play } from "lucide-react";
import { formatCount, formatTime } from "@/lib/format";

const thumb = (v) => (v.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}` : undefined);

export const VideoCard = ({ video, actions }) => (
  <div data-testid={`video-card-${video.id}`} className="group space-y-2.5">
    <Link to={`/watch/${video.id}`} data-testid={`video-link-${video.id}`} className="relative block aspect-video overflow-hidden rounded-2xl border border-white/10 bg-nw-surface">
      <img src={thumb(video)} alt={video.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <span className="absolute inset-0 m-auto grid h-12 w-12 place-items-center rounded-full bg-nw-red/90 text-white opacity-0 transition-opacity group-hover:opacity-100"><Play className="h-5 w-5 translate-x-[1px]" fill="currentColor" /></span>
      <span className="absolute bottom-2 right-2 rounded bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-slate-200">{formatTime(video.duration_seconds)}</span>
      <span className="absolute top-2 left-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-200">{video.category}</span>
    </Link>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/watch/${video.id}`} className="line-clamp-2 font-display font-semibold leading-snug text-white hover:text-nw-red transition-colors">{video.title}</Link>
        <p className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
          {video.owner && <Link to={`/u/${video.owner.username}`} className="truncate hover:text-white">@{video.owner.username}</Link>}
          <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{formatCount(video.view_count)}</span>
          <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{formatCount(video.like_count)}</span>
        </p>
      </div>
      {actions}
    </div>
  </div>
);

export const ShortThumb = ({ video, actions }) => (
  <div data-testid={`short-thumb-${video.id}`} className="group relative shrink-0 w-32 sm:w-40">
    <Link to={`/shorts?start=${video.id}`} className="relative block aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-nw-surface">
      <img src={thumb(video)} alt={video.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
      <p className="absolute bottom-2 left-2 right-2 line-clamp-2 text-xs font-semibold text-white">{video.title}</p>
      <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] text-slate-200"><Eye className="h-3 w-3" />{formatCount(video.view_count)}</span>
    </Link>
    {actions && <div className="mt-1 flex justify-end">{actions}</div>}
  </div>
);

export const VideoGrid = ({ videos, empty, renderActions }) =>
  videos.length === 0 ? (
    <p data-testid="video-list-empty" className="rounded-2xl border border-dashed border-white/15 p-6 text-sm text-slate-500">{empty}</p>
  ) : (
    <div data-testid="video-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {videos.map((v) => <VideoCard key={v.id} video={v} actions={renderActions?.(v)} />)}
    </div>
  );

export const ShortsRow = ({ videos, renderActions }) => (
  <div data-testid="shorts-row" className="flex gap-3 overflow-x-auto -mx-4 px-4 pb-2 snap-x scrollbar-none">
    {videos.map((v) => <ShortThumb key={v.id} video={v} actions={renderActions?.(v)} />)}
  </div>
);
